/**
 * @bos/portal3 — the client-side auth state, the single source of truth the whole SPA
 * reads. On mount it probes `auth.me` (the httpOnly session cookie rides along via the
 * client's `credentials: 'include'`), so a refresh re-establishes the session with no
 * token in JS — the cookie the browser holds is never readable here, which is the point.
 *
 * Exposes `login` / `register` / `logout`, each of which updates `user`/`status` on
 * success and rethrows a friendly, already-mapped message on failure for the forms to
 * show. No react-query dependency: a small context + one effect is all M2 needs.
 */
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { TRPCClientError } from '@trpc/client';
import type { SafeUser } from '@bos/portal3-server';
import { client } from '../rpc/client';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  readonly status: AuthStatus;
  readonly user: SafeUser | null;
  /** Re-read the session from the server (used on mount; safe to call again). */
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (input: { email: string; password: string; displayName: string }) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Update the profile display name (M3). On success the returned SafeUser replaces the
   * one in state, so the header/dashboard reflect it immediately (and, because the row
   * changed, a refresh re-probes the same value). Rethrows a friendly message on failure.
   */
  updateDisplayName: (displayName: string) => Promise<void>;
  /**
   * Change the password (M3). The current session stays valid (the server keeps it and
   * revokes only OTHER sessions), so this never logs the user out. Rethrows a friendly
   * message on failure — notably the wrong-current-password case.
   */
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Turn any thrown auth error into a message safe to render. The server sends friendly
 * messages for CONFLICT (duplicate email) and UNAUTHORIZED (bad credentials); a zod
 * BAD_REQUEST arrives as a JSON array of issues, so we surface the first issue's text.
 */
export function authErrorMessage(err: unknown): string {
  if (err instanceof TRPCClientError) {
    const raw: string = err.message ?? '';
    try {
      const issues = JSON.parse(raw) as Array<{ message?: string }>;
      if (Array.isArray(issues) && issues[0]?.message) return issues[0].message;
    } catch {
      /* not a zod issue array — fall through to the plain message */
    }
    return raw || 'Something went wrong. Please try again.';
  }
  return 'Could not reach the server. Please try again.';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SafeUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');

  const refresh = useCallback(async () => {
    try {
      const me = await client.auth.me.query();
      setUser(me);
      setStatus(me ? 'authenticated' : 'anonymous');
    } catch {
      // Network/server error on the probe: treat as logged-out rather than hang.
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const me = await client.auth.login.mutate({ email, password });
    setUser(me);
    setStatus('authenticated');
  }, []);

  const register = useCallback(
    async (input: { email: string; password: string; displayName: string }) => {
      const me = await client.auth.register.mutate(input);
      setUser(me);
      setStatus('authenticated');
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await client.auth.logout.mutate();
    } finally {
      // Whatever the server said, the client is now logged out.
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  const updateDisplayName = useCallback(async (displayName: string) => {
    const me = await client.account.updateDisplayName.mutate({ displayName });
    setUser(me);
    setStatus('authenticated');
  }, []);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      // The server keeps THIS session valid, so there's no user-state change here — a
      // thrown error (e.g. wrong current password) propagates to the form.
      await client.account.changePassword.mutate({ currentPassword, newPassword });
    },
    [],
  );

  const value: AuthContextValue = {
    status,
    user,
    refresh,
    login,
    register,
    logout,
    updateDisplayName,
    changePassword,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Read the auth context. Throws if used outside <AuthProvider> (a wiring bug). */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
